<template>
  <div :class="[outerClass, colSize]">
    <label v-if="label" class="block" :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
      :style="labelStyle">{{
        __(label)
      }}</label>
    <TreeSelect class="w-full" :display="display" :class="{ 'required-field': hasRequiredAttr }"
      :disabled="isFormDisabled" :name="fieldName" :validation="validation" :selectionMode="selectionMode"
      :options="options" :placeholder="__('Select upper level')" @filter="filter" v-focus="isFocus" v-model="field"
      :pt="treeSelectPt" :dt="treeSelectTokens" :unstyled="false" />
    <input type="hidden" :name="fieldName" :value="JSON.stringify(field)" />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import TreeSelect from 'primevue/treeselect';
import { __ } from '@pages/Helper/utils.js'

const props = defineProps({
  fieldName: {
    type: String,
    required: true,
  },
  validation: {
    type: [Array, String],
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  size: {
    type: String,
    default: null
  },
  isFocus: {
    type: Boolean,
    default: false
  },
  isFormDisabled: {
    type: Boolean,
    default: false
  },
  labelClass: {
    type: String,
    default: null
  },
  labelStyle: {
    type: [Object, String],
    default: null
  },
  options: {
    type: Array,
    default: [],
    required: true
  },
  filter: {
    type: Function,
    required: false,
  },
  selectionMode: {
    type: String,
    default: 'single'
  },
  inRepeater: {
    type: Boolean,
    default: false
  },
});

const field = defineModel('field');
const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const outerClass = ref('field');
const display = ref(props.selectionMode === 'multiple' ? 'chip' : 'comma');
const transformed = ref(false);

// ------------------------------------------------------------------------------

const treeSelectTokens = {
  root: {
    background: '#ffffff',
    borderColor: '#d1d5db',
    hoverBorderColor: '#9ca3af',
    focusBorderColor: '#3b82f6',
    borderRadius: '6px',
  },
  label: {
    color: '#1e40af',
  },
  panel: { shadow: '0 4px 24px rgba(0,0,0,0.10)' },
  overlay: {
    background: '#ffffff',
    shadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderRadius: '6px',
    borderColor: '#e5e7eb'
  }
};

const treeSelectPt = {
  root: { class: 'w-full min-h-[2.5rem] flex items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-sm shadow-sm transition' },
  label: { class: 'text-gray-800 py-2 px-3' },
  trigger: { class: 'text-primary-500' },
  tree: {
    root: { class: 'p-4' },
    nodeContent: ({ context }) => ({
      class: [
        'flex items-center p-2 rounded-md transition-all duration-200 cursor-pointer',
        {
          'bg-primary-50 text-primary-700 font-medium': context.selected,
          'hover:bg-gray-100 text-gray-700': !context.selected
        }
      ]
    }),
    nodeLabel: { class: 'text-sm' },
    nodeToggleButton: {
      class: 'w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-transparent'
    }
  }
};

// ------------------------------------------------------------------------------

watch(() => field.value, (newValue, oldValue) => {
  if (!transformed.value) {
    if (typeof field.value === 'number' && field.value !== null) {
      field.value = { [field.value]: true };
    }
    else if (typeof field.value === 'object' && field.value !== null) {
      let tmp = {}
      for (let elem in field.value) {
        if (typeof field.value[elem] !== "boolean") {
          tmp[field.value[elem]] = true;
        } else {
          tmp = field.value;
        }
      }
      field.value = { ...tmp };
    }
  }
  transformed.value = true
})

// ------------------------------------------------------------------------------

if (props.inRepeater) {
  outerClass.value = null;
}


</script>

<style lang="scss">
.p-tree-node-content {
  padding: 8px;
}

.p-tree-node-children {
  margin-left: 8px;
}

.p-tree-node-toggle-button {
  margin-right: 8px;
}

.p-tree-node-selected {
  background-color: #b1b1b1 !important;
  color: #fff !important;
  font-weight: 500;
  border-radius: 6px;
  margin: 2px;
}

.p-chip.p-component.p-treeselect-chip {
  background-color: #e0e0e0 !important;
  color: #333 !important;
  border-radius: 6px;
  padding: 2px 4px;
  margin: 2px;
}
</style>
